/**
 * Groups API — typed migration of GroupsAPI.js
 * All 7 original endpoints preserved with TypeScript types.
 */
import apiClient from './client';
import type { Group, ApiResult } from './types';

const GroupsAPI = {
  /** Get groups for a user (or current user if no userId) */
  async getMyGroups(userId?: string): Promise<Group[]> {
    const { data } = await apiClient.get<Group[]>('/v3/classes', {
      params: userId ? { userId } : undefined,
    });
    return data;
  },

  /** Get single group with student details */
  async getGroupWithStudents(id: string): Promise<Group> {
    const { data } = await apiClient.get<Group>(
      `/classes/${id}/with-students`,
    );
    return data;
  },

  /** Create a new group */
  async createGroup(name: string): Promise<{ group: Group }> {
    const { data } = await apiClient.post<{ group: Group }>('/v3/classes', {
      name,
    });
    return data;
  },

  /** Update group properties */
  async updateGroup(payload: Partial<Group> & { id: string }): Promise<Group> {
    const { data } = await apiClient.put<Group>('/v3/classes', payload);
    return data;
  },

  /** Delete a group */
  async deleteGroup(id: string): Promise<void> {
    try {
      await apiClient.delete(`/v3/classes/${id}`);
    } catch {
      await apiClient.delete(`/v2/class/${id}`);
    }
  },

  /** Student joins a group via invitation code */
  async addToClass(
    userId: string,
    invitationCode: string,
  ): Promise<Group[]> {
    const { data } = await apiClient.post<ApiResult<Group[]>>(
      '/add_to_class',
      { userId, invitationCode },
    );
    if (data.error) {
      throw new Error(data.error.message);
    }
    // Return refreshed groups list
    return this.getMyGroups();
  },

  /**
   * Teacher adds a student to a group by email.
   * Backend: POST /add_to_class_by_email  { email, classId }
   */
  async addToClassByEmail(
    email: string,
    classId: string,
  ): Promise<{ studentId: string }> {
    const { data } = await apiClient.post<ApiResult<{ studentId: string }>>(
      '/add_to_class_by_email',
      { email: email.toLowerCase().trim(), classId },
    );
    if (data.error) {
      throw new Error(data.error.message);
    }
    return data as unknown as { studentId: string };
  },

  /** Remove a user from a group */
  async removeFromClass(userId: string, classId: string): Promise<Group[]> {
    await apiClient.post('/remove_user_from_class', {
      userId,
      classId,
    });
    return this.getMyGroups();
  },
};

export default GroupsAPI;
